import { useState, useEffect } from "react";
import GraphiqueBarres from "../components/GraphiqueBarres";
import { createChangeManagement, getChangeManagementDashboard } from "../services/api";

// ===================================================================
// The Crowd v2 — Change Management
//
// Adaptation v2 de pages/ChangeManagement.js. La logique est reprise a
// l'identique (memes appels API, meme charge utile) ; seuls le balisage
// et les classes changent.
//
// Acces : ouvert a tous les collaborateurs dans la v2 (l'ancienne page
// reste reservee aux managers et directeurs, dans leur vue).
//
// Disposition plein ecran : formulaire a gauche, synthese a droite.
// Apres creation, le recapitulatif du ticket remplace le formulaire ;
// la synthese reste affichee et se rafraichit.
//
// L'ancienne page reste en place sur /change-management tant que la
// v2 n'a pas remplace l'interface actuelle.
// ===================================================================

const PRIORITIES = ["Highest", "High", "Medium", "Low", "Lowest"];
const CLASSIFICATIONS = ["Standard", "Majeur"];
const IMPACTS_SECURITE = ["Aucun", "Faible", "Significatif"];
const PERIMETRES = ["Infrastructure", "Reseau", "M365", "Azure", "Securite", "Autres"];
const STATUTS = ["Ouvert", "Travail en cours", "Terminé"];

const PERIODES = [
  { value: "mois", label: "30 jours" },
  { value: "annee", label: "Année" },
  { value: "tout", label: "Tout" },
];

const emptyForm = () => ({
  summary: "",
  description: "",
  priority: "Medium",
  classification: "",
  impact_securite: "",
  perimetre: "",
  plan_rollback: "",
  date_prevue: "",
  statut: "Ouvert",
});

// ---------------------------------------------------------------------------
// Synthese CHG
// ---------------------------------------------------------------------------
function ChangeManagementDashboard({ refreshKey }) {
  const [periode, setPeriode] = useState("tout");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getChangeManagementDashboard(periode)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [periode, refreshKey]);

  return (
    <section className="v2-chg-synthese" aria-labelledby="v2-chg-synthese-titre">
      <div className="v2-bloc-entete">
        <h2 id="v2-chg-synthese-titre" className="v2-bloc-titre">Synthèse</h2>
        <div className="v2-bascule" role="group" aria-label="Période">
          {PERIODES.map((p) => (
            <button
              key={p.value}
              type="button"
              className={`v2-bascule-choix${periode === p.value ? " actif" : ""}`}
              aria-pressed={periode === p.value}
              onClick={() => setPeriode(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="v2-chargement">Chargement des données…</div>}
      {error && <div className="v2-msg v2-msg-erreur">{error}</div>}

      {!loading && !error && data && (
        <>
          <div className="v2-chg-stats">
            <div className="v2-stat">
              <span className="v2-stat-libelle">Créés</span>
              <span className="v2-stat-valeur">{data.totaux.total_crees}</span>
            </div>
            <div className="v2-stat">
              <span className="v2-stat-libelle">En cours</span>
              <span className="v2-stat-valeur">{data.totaux.total_en_cours}</span>
            </div>
            <div className="v2-stat">
              <span className="v2-stat-libelle">Terminés</span>
              <span className="v2-stat-valeur">{data.totaux.total_termines}</span>
            </div>
          </div>

          <div className="v2-chg-graphiques">
            <div className="v2-panneau">
              <h3 className="v2-panneau-titre">Par classification</h3>
              <GraphiqueBarres data={data.par_classification} labelKey="classification" valueKey="total" />
            </div>
            <div className="v2-panneau">
              <h3 className="v2-panneau-titre">Par impact sécurité</h3>
              <GraphiqueBarres data={data.par_impact_securite} labelKey="impact_securite" valueKey="total" />
            </div>
            <div className="v2-panneau">
              <h3 className="v2-panneau-titre">Par périmètre</h3>
              <GraphiqueBarres data={data.par_perimetre} labelKey="perimetre" valueKey="total" />
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------
export default function ChangeManagementV2({ currentUser }) {
  const [form, setForm] = useState(emptyForm());
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [refreshKey, setRefreshKey] = useState(0); // pour rafraichir la synthese apres creation

  // Collaborateur introuvable en base : rien a afficher
  if (!currentUser) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setForm(emptyForm());
    setStatus("idle");
    setResult(null);
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.summary.trim()) return;

    setStatus("loading");
    setErrorMsg("");

    try {
      const payload = { ...form, collaborateur_id: currentUser.id };
      if (!payload.description) delete payload.description;
      if (!payload.classification) delete payload.classification;
      if (!payload.impact_securite) delete payload.impact_securite;
      if (!payload.perimetre) delete payload.perimetre;
      if (!payload.plan_rollback) delete payload.plan_rollback;
      if (!payload.date_prevue) delete payload.date_prevue;

      const data = await createChangeManagement(payload);
      setResult(data);
      setStatus("success");
      setRefreshKey((k) => k + 1); // rafraichit la synthese
    } catch (err) {
      setErrorMsg(err.message || "Erreur inattendue.");
      setStatus("error");
    }
  };

  const enCours = status === "loading";

  return (
    <div className="v2-page v2-page-module">
      <header className="v2-module-entete">
        <h1 className="v2-module-titre">Change Management</h1>
        <p className="v2-sous-titre">Créer un ticket CHG dans Jira et suivre la synthèse des changements.</p>
      </header>

      <div className="v2-chg-colonnes">
        {/* ── Colonne gauche : formulaire, ou recapitulatif apres creation ── */}
        {status === "success" && result ? (
          <section className="v2-panneau v2-chg-succes" aria-live="polite">
            <span className="v2-chg-succes-icone" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3.5 8.4 6.6 11.4 12.5 4.8" />
              </svg>
            </span>
            <h2 className="v2-bloc-titre">Ticket créé</h2>
            <p className="v2-chg-succes-cle">{result.ticket_key}</p>
            <p className="v2-chg-info">Statut : <strong>{result.statut}</strong></p>
            <div className="v2-actions v2-actions-centre">
              <button type="button" onClick={handleReset} className="v2-btn v2-btn-secondaire">
                Nouveau ticket
              </button>
              <a href={result.ticket_url} target="_blank" rel="noopener noreferrer" className="v2-btn v2-btn-principal">
                Ouvrir dans Jira
              </a>
            </div>
          </section>
        ) : (
          <section className="v2-panneau v2-chg-formulaire" aria-labelledby="v2-chg-form-titre">
            <h2 id="v2-chg-form-titre" className="v2-bloc-titre">Nouveau ticket</h2>

            <form className="v2-formulaire" onSubmit={handleSubmit} noValidate>
              <div className="v2-champ">
                <label className="v2-label" htmlFor="summary">
                  Résumé <span className="v2-requis" aria-hidden="true">*</span>
                </label>
                <input
                  id="summary"
                  name="summary"
                  type="text"
                  value={form.summary}
                  onChange={handleChange}
                  placeholder="Titre court et explicite du changement"
                  required
                  disabled={enCours}
                />
              </div>

              <div className="v2-champ">
                <label className="v2-label" htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Contexte, objectif, détails du changement…"
                  rows={4}
                  disabled={enCours}
                />
              </div>

              <div className="v2-champs-ligne">
                <div className="v2-champ">
                  <label className="v2-label" htmlFor="priority">Priorité</label>
                  <select id="priority" name="priority" value={form.priority} onChange={handleChange} disabled={enCours}>
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                <div className="v2-champ">
                  <label className="v2-label" htmlFor="statut">Statut</label>
                  <select id="statut" name="statut" value={form.statut} onChange={handleChange} disabled={enCours}>
                    {STATUTS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="v2-champs-ligne">
                <div className="v2-champ">
                  <label className="v2-label" htmlFor="classification">Classification</label>
                  <select id="classification" name="classification" value={form.classification} onChange={handleChange} disabled={enCours}>
                    <option value="">— Sélectionner —</option>
                    {CLASSIFICATIONS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="v2-champ">
                  <label className="v2-label" htmlFor="impact_securite">Impact sécurité</label>
                  <select id="impact_securite" name="impact_securite" value={form.impact_securite} onChange={handleChange} disabled={enCours}>
                    <option value="">— Sélectionner —</option>
                    {IMPACTS_SECURITE.map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="v2-champs-ligne">
                <div className="v2-champ">
                  <label className="v2-label" htmlFor="perimetre">Périmètre</label>
                  <select id="perimetre" name="perimetre" value={form.perimetre} onChange={handleChange} disabled={enCours}>
                    <option value="">— Sélectionner —</option>
                    {PERIMETRES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                <div className="v2-champ">
                  <label className="v2-label" htmlFor="date_prevue">Date prévue</label>
                  <input
                    id="date_prevue"
                    name="date_prevue"
                    type="datetime-local"
                    value={form.date_prevue}
                    onChange={handleChange}
                    disabled={enCours}
                  />
                </div>
              </div>

              <div className="v2-champ">
                <label className="v2-label" htmlFor="plan_rollback">Plan de rollback</label>
                <textarea
                  id="plan_rollback"
                  name="plan_rollback"
                  value={form.plan_rollback}
                  onChange={handleChange}
                  placeholder="Procédure de retour arrière en cas d'échec…"
                  rows={3}
                  disabled={enCours}
                />
              </div>

              <p className="v2-chg-info">
                Assigné automatiquement à : <strong>{currentUser?.jira_display_name || currentUser?.nom || "vous"}</strong>
              </p>

              {status === "error" && <div className="v2-msg v2-msg-erreur">{errorMsg}</div>}

              <div className="v2-actions">
                <button type="button" className="v2-btn v2-btn-secondaire" onClick={handleReset} disabled={enCours}>
                  Réinitialiser
                </button>
                <button type="submit" className="v2-btn v2-btn-principal" disabled={enCours || !form.summary.trim()}>
                  {enCours ? "Création en cours…" : "Créer le ticket CHG"}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* ── Colonne droite : synthese ── */}
        <ChangeManagementDashboard refreshKey={refreshKey} />
      </div>
    </div>
  );
}
